// @flow
import { compose, pure } from 'recompose';
import React, { type Element } from 'react';
import { connect } from 'react-redux';
import { groupBy } from 'lodash';
import { legacyAnalytics, getErrorMessage } from 'angieslist-app-platform';
import { Alert, Col, Input, EllipsedPagination, Row } from 'angieslist-react-components';
import paginationUtils from 'angieslist-app-platform/lib/common/utils/paginationUtils';
import AddedBusinessHandler from './AddedBusinessHandler';
import BusinessInfoPanel from '../../../../app/browser/BusinessInfoPanel';
import './businessList.scss';
import type { UserState } from '../../../../app/common/user/userReducer';
import serviceProviderActions from '../../../../app/common/serviceProvider/serviceProviderActions';
import type { ServiceProvider, ServiceProviderCacheState } from '../../../../app/types';
import type { BusinessListReducerState } from '../../common/businessListReducer';
import businessListAnalytics from './businessListAnalytics';

const prosPerPage = 20;
const activityLocation = 'SP : Business List';

export type BusinessListProps = {
  // redux
  businessList: BusinessListReducerState,
  serviceProviderCache: ServiceProviderCacheState,
  // redux serviceProviderActions
  getCurrentUsersServiceProviders: typeof serviceProviderActions.getCurrentUsersServiceProviders,
  user: UserState,
};

type ServiceProviderDetailsWithEntitlements = {
  serviceProvider: ServiceProvider,
  entitlements: any,
}
const renderInfoPanel = () => ({
  serviceProvider,
  entitlements,
}: ServiceProviderDetailsWithEntitlements) => (
  <BusinessInfoPanel
    key={serviceProvider.id}
    activityLocation={activityLocation}
    showBusinessListLink={false}
    serviceProvider={serviceProvider}
    entitlements={entitlements}
  />);

const renderSortDropdown = (currentSort, getCurrentUsersServiceProviders) => (
  <div className="form-inline business-list--sort-container">
    <Input
      type="select"
      label={<small>Sort by:</small>}
      id="business-list--sort-by"
      value={currentSort}
      labelClassName="sort-by-label"
      onChange={event => getCurrentUsersServiceProviders({
        offset: 0,
        limit: prosPerPage,
        sortBy: event.target.value,
      })}
    >
      <option value="name">Business Name</option>
      <option value="overall_gpa">Ratings (5-1)</option>
      <option value="overall_reviews">Reviews</option>
      <option value="alId">SPID</option>
    </Input>
  </div>
);

export const BusinessListComponent = (props: BusinessListProps): Element<"div"> => {
  const {
    businessList: {
      getCurrentUsersServiceProviders: {
        request,
        response,
        isLoading,
        error,
      },
      getServiceProvider: {
        isLoading: isLoadingDetails,
        error: errorDetails,
      },
    },
    serviceProviderCache,
    getCurrentUsersServiceProviders,
    user: {
      userInfo: {
        response: userResponse,
      },
    },
  } = props;
  const { serviceProviders = [], total = 0 } = response || {};
  const { entitlements = [] } = userResponse || {};
  const entitlementsBySPID = groupBy(entitlements.filter(e => e.scope === 'ServiceProvider'), e => e.scopeId);
  const serviceProviderDetails = serviceProviders.map(x => ({
    ...serviceProviderCache[String(x.id)],
    ...x,
  }));
  const serviceProviderDetailsWithEntitlements = serviceProviderDetails.map(x => ({
    serviceProvider: x,
    entitlements: entitlementsBySPID[`${x.id}`],
  }));
  const {
    limit = prosPerPage, offset = 0, sortBy = 'name',
  } = request || {};
  const pageIndex = paginationUtils.getPageNum(offset, limit) - 1;
  const pageCount = paginationUtils.getPageCount(total, limit);
  return (
    <div className="business-list">
      <Row className="business-list--header-row">
        <Col xs={12} sm={8}>
          <If condition={serviceProviders.length > 1}>
            <AddedBusinessHandler />
          </If>
          <h1 style={{ fontSize: 32 }}>
                Business List
            <If condition={isLoading || isLoadingDetails}>
              <i className="icon icon-load text-primary icon-pulse" />
            </If>
          </h1>
          <If condition={error || errorDetails}>
            <Alert bsStyle="danger">
              {getErrorMessage(error || errorDetails)}
            </Alert>
          </If>
        </Col>
        <Col sm={4}>
          <If condition={serviceProviders.length > 1}>
            {renderSortDropdown(sortBy, getCurrentUsersServiceProviders)}
          </If>
        </Col>
      </Row>
      <hr className="hidden-xs" style={{ margin: '4px 0 32px 0' }} />
      {
            serviceProviderDetailsWithEntitlements
              .map(renderInfoPanel())
          }
      <If condition={pageCount > 1}>
        <Row>
          <Col xs={12}>
            <EllipsedPagination
              pageCount={pageCount}
              pageIndex={pageIndex}
              onChange={index => getCurrentUsersServiceProviders({
                    offset: paginationUtils.getOffset(index + 1, prosPerPage),
                    limit: prosPerPage,
                    sortBy,
                  })}
            />
          </Col>
        </Row>
      </If>
    </div>
  );
};

export default (compose(
  legacyAnalytics({
    ruleset: businessListAnalytics,
  }),
  connect(state => ({
    businessList: state.businessList,
    serviceProviderCache: state.serviceProviderCache,
    user: state.user,
  }), {
    ...serviceProviderActions,
  }),
  pure,
)(BusinessListComponent): any);



// WEBPACK FOOTER //
// ./src/modules/home/browser/components/BusinessList.js